"use client";

import { MindstringPrototype } from "@/generated/fastapi";
import {
  useAuth,
  useBacklogSearchControls,
  useCheckSearchControls,
  useMeiliRefreshState,
  useSelectSearchControls,
} from "@/state/globalStore";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import { Meilisearch } from "meilisearch";
import { useCallback, useMemo } from "react";
import { buildHybridSearchParams } from "./helpers";
import { fetchMeiliUserToken } from "./fetchMeiliUserToken";
import {
  type MeiliThoughtIndexName,
  searchThoughtDocumentsWithClient,
} from "./searchThoughtDocuments";
import { logMeiliPerf } from "./debug";

export type MeiliRuntimeSurface =
  | "backlog"
  | "studio_check"
  | "studio_select";

export function useMeiliRuntime(options?: {
  surface?: MeiliRuntimeSurface;
  prototype?: MindstringPrototype;
}) {
  const { accessToken, meiliUserToken } = useAuth();
  const { meiliRefreshStamp } = useMeiliRefreshState();
  const selectControls = useSelectSearchControls();
  const checkControls = useCheckSearchControls();
  const backlogControls = useBacklogSearchControls();
  const surface = options?.surface;
  const prototype = options?.prototype;

  const semanticRatio = useMemo(() => {
    switch (surface) {
      case "studio_select":
        return selectControls.semanticRatio;
      case "studio_check":
        return checkControls.semanticRatio;
      case "backlog":
        return backlogControls.semanticRatio;
      default:
        return 0;
    }
  }, [
    backlogControls.semanticRatio,
    checkControls.semanticRatio,
    selectControls.semanticRatio,
    surface,
  ]);

  const searchClientRefreshStamp = useMemo(() => {
    switch (surface) {
      case "studio_select":
        return String(
          prototype
            ? meiliRefreshStamp["studio_thoughts"][prototype]
            : Object.values(meiliRefreshStamp["studio_thoughts"]).join(":"),
        );
      case "studio_check":
        return String(
          prototype
            ? meiliRefreshStamp["studio_suggested_thoughts"][prototype]
            : Object.values(meiliRefreshStamp["studio_suggested_thoughts"]).join(
                ":",
              ),
        );
      case "backlog":
        return String(meiliRefreshStamp["backlog_origins"]);
      default:
        return "runtime-static";
    }
  }, [meiliRefreshStamp, prototype, surface]);

  const getUserToken = useCallback(async () => {
    const session = await fetchMeiliUserToken(accessToken);
    return session?.userToken ?? null;
  }, [accessToken]);

  const meiliJsClient = useMemo(() => {
    if (!meiliUserToken) {
      return undefined;
    }

    return new Meilisearch({
      host: process.env.NEXT_PUBLIC_MEILI_URL as string,
      apiKey: meiliUserToken,
    });
  }, [meiliUserToken]);

  const searchClient = useMemo(() => {
    if (!meiliUserToken) {
      return undefined;
    }

    const hybridConfig = buildHybridSearchParams(semanticRatio);

    logMeiliPerf("searchClient:create", {
      surface: surface ?? "shared",
      semanticRatio,
      refreshStamp: searchClientRefreshStamp,
      hybridEnabled: !!hybridConfig,
    });

    return instantMeiliSearch(
      process.env.NEXT_PUBLIC_MEILI_URL as string,
      meiliUserToken,
      {
        finitePagination: false,
        ...(hybridConfig && {
          meiliSearchParams: {
            hybrid: hybridConfig,
          },
        }),
      },
    ).searchClient;
  }, [meiliUserToken, searchClientRefreshStamp, semanticRatio, surface]);

  const searchThoughtDocuments = useCallback(
    async function <T extends Record<string, unknown>>({
      indexName,
      originId,
      situationId,
      prototype,
      items_ids,
      languageFilters,
      limit,
      queryText,
      semanticRatio = 0,
      sortBy,
    }: {
      indexName: MeiliThoughtIndexName;
      originId?: string;
      situationId?: string;
      prototype?: MindstringPrototype;
      items_ids?: string[];
      languageFilters?: string[];
      limit?: number;
      queryText?: string;
      semanticRatio?: number;
      sortBy?: string;
    }): Promise<T[]> {
      const startedAt =
        typeof performance !== "undefined" ? performance.now() : Date.now();

      const results = await searchThoughtDocumentsWithClient<T>({
        meiliJsClient,
        indexName,
        originId,
        situationId,
        prototype,
        items_ids,
        languageFilters,
        limit,
        queryText,
        semanticRatio,
        sortBy,
      });

      const finishedAt =
        typeof performance !== "undefined" ? performance.now() : Date.now();

      logMeiliPerf("document-search", {
        indexName,
        hasQuery: !!queryText,
        itemsCount: items_ids?.length ?? 0,
        limit: limit ?? null,
        semanticRatio,
        sortBy: sortBy ?? null,
        durationMs: Math.round(finishedAt - startedAt),
      });

      return results;
    },
    [meiliJsClient],
  );

  return {
    getUserToken,
    meiliJsClient,
    searchClient,
    searchThoughtDocuments,
  };
}
